import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const AboutUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const scrollToSection = (sectionId) => {
    // If using react-router, navigate to home first
    // navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <>
      <Helmet>
        <title>About Dr. Zain Memon | Century Longevity & Aesthetics</title>
        <meta name="description" content="Meet Dr. Zain Memon, board-certified internist and founder of Century Aesthetics. NYIT graduate, specializing in aesthetic medicine, Botox, fillers, and preventative care in Forest Hills, NY." />
        <meta name="keywords" content="Dr. Zain Memon, board certified doctor, internal medicine, aesthetic doctor, Forest Hills physician, medical spa doctor, Botox specialist" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/about" />
        
        <meta property="og:title" content="About Dr. Zain Memon | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Meet our board-certified physician specializing in aesthetic medicine and preventative care." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/about" />
      </Helmet>

      <section ref={ref} className="min-h-screen bg-cream pt-36 pb-16" aria-label="About Dr. Zain Memon">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-olive-darkest mb-4 tracking-wide">
            Meet Dr. Zain Memon
          </h1>
          <div className="w-20 h-0.5 bg-gold mx-auto"></div>
        </motion.div>

        <div className="grid lg:grid-cols-7 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 relative"
          >
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="/zain.jpg" 
                alt="Dr. Zain Memon - Board Certified Internal Medicine Physician at Century Longevity & Aesthetics" 
                className="w-full h-auto object-cover"
              />
              {/* Decorative border */}
              <div className="absolute inset-0 border-4 border-gold/20 rounded-lg pointer-events-none"></div>
            </div>
            {/* Decorative elements */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-olive-dark/10 rounded-lg -z-10"></div>
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/10 rounded-lg -z-10"></div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-5 space-y-4"
          >
            <article className="prose prose-lg max-w-none">
              <p className="text-olive-dark text-base sm:text-lg leading-relaxed mb-4">
                Dr. Zain Memon is a highly skilled internist and primary care doctor who obtained his doctoral degree in medicine from the New York Institute of Technology, New York College of Osteopathic Medicine. He completed his residency in internal medicine at Northwell Lenox Hill Hospital. Currently, Dr. Memon practices at Century Medical Services in Forest Hills, NY.
              </p>

              <p className="text-olive-dark text-base sm:text-lg leading-relaxed mb-4">
                Certified by the American Board of Internal Medicine, Dr. Memon is a member of the American Academy of Family Physicians and the American College of Physicians. Fluent in English, Spanish, Hindi, and Urdu, he is well-equipped to serve patients from diverse cultural backgrounds. Patients seek Dr. Memon's expertise for hypertension, diabetes, cholesterol management, age-related vaccinations, and preventative cancer screening. Additionally, he is certified in administering injectables, including Botox and Dermal Fillers.
              </p>

              <p className="text-olive-dark text-base sm:text-lg leading-relaxed">
                Born and raised in Queens, Dr. Memon believes the community provides invaluable exposure to pathology and education. Apart from his medical practice, he enjoys spending time with his family, exploring culinary arts, and traveling the world.
              </p>
            </article>

            {/* Credentials */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-olive-dark/10" role="list" aria-label="Dr. Memon's Credentials">
              <div className="bg-nude/30 p-4 rounded-lg shadow-sm border border-gold/20" role="listitem">
                <h3 className="font-semibold text-olive-darkest mb-2 text-sm sm:text-base">Education</h3>
                <p className="text-olive-dark text-sm leading-relaxed">NYIT College of Osteopathic Medicine</p>
              </div>
              <div className="bg-nude/30 p-4 rounded-lg shadow-sm border border-gold/20" role="listitem">
                <h3 className="font-semibold text-olive-darkest mb-2 text-sm sm:text-base">Residency</h3>
                <p className="text-olive-dark text-sm leading-relaxed">Northwell Lenox Hill Hospital</p>
              </div>
              <div className="bg-nude/30 p-4 rounded-lg shadow-sm border border-gold/20" role="listitem">
                <h3 className="font-semibold text-olive-darkest mb-2 text-sm sm:text-base">Board Certification</h3>
                <p className="text-olive-dark text-sm leading-relaxed">American Board of Internal Medicine</p>
              </div>
              <div className="bg-nude/30 p-4 rounded-lg shadow-sm border border-gold/20" role="listitem">
                <h3 className="font-semibold text-olive-darkest mb-2 text-sm sm:text-base">Languages</h3>
                <p className="text-olive-dark text-sm leading-relaxed">English, Spanish, Hindi, Urdu</p>
              </div>
            </div>

            {/* CTA Button */}
            <motion.button
              whileHover={{ y: -2, boxShadow: '0 10px 30px rgba(201, 169, 97, 0.4)' }}
              whileTap={{ scale: 0.98 }}
              onClick={() => scrollToSection('contact')}
              className="bg-gold text-olive-darkest px-8 py-3 rounded font-semibold text-sm tracking-wider uppercase hover:bg-gold/90 transition-all duration-300 shadow-lg"
              aria-label="Schedule an appointment with Dr. Memon"
            >
              Schedule an Appointment
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
    </>
  );
};

export default AboutUs;